import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { requiresAds, resolvePatTier } from "@vninvestcharts/engine-core";
const DEFAULT_CYCLE_MS = 5 * 60 * 1000;
const DEFAULT_COUNTDOWN = 5;
const POPUP_DISPLAY_DURATION = 30000;
const PORTAL_ROOT_ID = "vninvestcharts-widget-ad-root";
/** Default activation date for ad dialog. Before this date, ads are suppressed. */
const ADV_ACTIVATION_DATE = new Date('2027-01-01T00:00:00Z').getTime();
// Inline styles (self-contained, theme-aware via CSS vars). Inline so widget
// CSS cannot override or hide the ad slot.
const STYLES = {
    bannerSlot: {
        position: "fixed",
        bottom: "15px",
        right: "15px",
        width: "200px",
        height: "50px",
        minHeight: "50px",
        background: "var(--vnsc-bg-elevated, rgba(24, 34, 47, 0.9))",
        border: "1px solid var(--vnsc-border-overlay, rgba(255, 255, 255, 0.1))",
        borderRadius: "8px",
        zIndex: 2147483646,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
        boxSizing: "border-box",
        animation: "vnscBannerSlideIn 0.3s ease-out",
        pointerEvents: "auto",
    },
    bannerSlotHidden: { display: "none" },
    bannerLink: {
        display: "block",
        width: "100%",
        height: "100%",
        textDecoration: "none",
        color: "inherit",
    },
    bannerImage: {
        width: "100%",
        height: "100%",
        objectFit: "cover",
        borderRadius: "7px",
        userSelect: "none",
    },
    popup: {
        position: "fixed",
        bottom: "75px",
        right: "15px",
        width: "200px",
        backgroundColor: "var(--vnsc-bg-popover, rgba(24, 34, 47, 0.95))",
        border: "1px solid var(--vnsc-border-overlay, rgba(255, 255, 255, 0.1))",
        borderRadius: "8px",
        padding: "12px",
        boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.5)",
        zIndex: 2147483647,
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        textAlign: "left",
        boxSizing: "border-box",
        animation: "vnscPopupSlideIn 0.3s ease-out",
        pointerEvents: "auto",
    },
    popupHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "2px" },
    popupBadges: { display: "flex", gap: "4px", alignItems: "center" },
    popupBadgePromo: {
        backgroundColor: "var(--vnsc-color-primary, #2962ff)",
        color: "var(--vnsc-text-on-primary, #ffffff)",
        fontSize: "8px",
        fontWeight: "bold",
        padding: "2px 4px",
        borderRadius: "3px",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
    },
    popupBadgeAd: {
        border: "1px solid var(--vnsc-border-overlay, rgba(255, 255, 255, 0.2))",
        color: "var(--vnsc-text-muted, rgba(255, 255, 255, 0.6))",
        fontSize: "8px",
        fontWeight: "bold",
        padding: "1px 3px",
        borderRadius: "3px",
    },
    popupCloseBtn: {
        background: "transparent",
        border: "1px solid var(--vnsc-border-overlay, rgba(255, 255, 255, 0.2))",
        color: "var(--vnsc-text-secondary, #94a3b8)",
        cursor: "pointer",
        fontSize: "14px",
        lineHeight: "1",
        padding: "2px",
        transition: "all 0.2s ease",
        borderRadius: "3px",
        width: "24px",
        height: "24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    popupMedia: {
        cursor: "pointer",
        overflow: "hidden",
        borderRadius: "4px",
        border: "1px solid var(--vnsc-border-overlay, rgba(255, 255, 255, 0.05))",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "50px",
        backgroundColor: "var(--vnsc-bg-hover, rgba(255, 255, 255, 0.02))",
    },
    popupMediaImg: { width: "100%", height: "100%", objectFit: "cover", userSelect: "none" },
    popupInfo: { display: "flex", flexDirection: "column", gap: "4px" },
    popupTitle: { margin: "0", color: "var(--vnsc-text-primary, #ffffff)", fontSize: "12px", fontWeight: 600, lineHeight: "1.3" },
    popupDescription: { margin: "0", color: "var(--vnsc-text-secondary, #94a3b8)", fontSize: "10px", lineHeight: "1.4" },
    popupActions: { display: "flex", gap: "8px", marginTop: "4px" },
    popupUpgradeBtn: {
        flex: 1,
        backgroundColor: "var(--vnsc-color-primary, #2962ff)",
        color: "var(--vnsc-text-on-primary, #ffffff)",
        border: "none",
        padding: "6px 12px",
        borderRadius: "4px",
        fontWeight: 600,
        fontSize: "11px",
        cursor: "pointer",
        transition: "background-color 0.2s ease",
    },
    popupCloseBtnSecondary: {
        flex: 1,
        backgroundColor: "var(--vnsc-bg-tertiary, #1f2937)",
        color: "var(--vnsc-text-muted, #6b7280)",
        border: "none",
        padding: "6px 12px",
        borderRadius: "4px",
        fontSize: "11px",
        fontWeight: 500,
        cursor: "not-allowed",
        transition: "all 0.2s ease",
    },
    popupCloseBtnSecondaryEnabled: {
        backgroundColor: "var(--vnsc-bg-elevated, #374151)",
        color: "var(--vnsc-text-secondary, #d1d5db)",
        cursor: "pointer",
    },
};
function ensureKeyframes() {
    if (typeof document === "undefined")
        return;
    const id = "vninvestcharts-widget-ad-keyframes";
    if (document.getElementById(id))
        return;
    const style = document.createElement("style");
    style.id = id;
    style.textContent = `
@keyframes vnscBannerSlideIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
@keyframes vnscPopupSlideIn { from { opacity: 0; transform: scale(0.95) translateY(10px); } to { opacity: 1; transform: scale(1) translateY(0); } }
`;
    document.head.appendChild(style);
}
function getPortalRoot() {
    if (typeof document === "undefined")
        return null;
    ensureKeyframes();
    let root = document.getElementById(PORTAL_ROOT_ID);
    if (!root) {
        root = document.createElement("div");
        root.id = PORTAL_ROOT_ID;
        root.style.position = "fixed";
        root.style.top = "0";
        root.style.left = "0";
        root.style.width = "100%";
        root.style.height = "100%";
        root.style.pointerEvents = "none";
        root.style.zIndex = "2147483645";
        document.body.appendChild(root);
    }
    return root;
}
function trackBeacon(url) {
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
        try {
            navigator.sendBeacon(url);
        }
        catch {
            /* silent */
        }
    }
}
export function EngineAdSlot({ pat, validatePat, campaign, fallback, clickTrackingUrl, impressionTrackingUrl, destinationUrl, imageSource, cycleMinutes = 5, countdownSeconds = DEFAULT_COUNTDOWN, onAdShow, onAdClose, onAdClick, locale, activationDate, }) {
    // Defense-in-depth: suppress ad before activation date
    const effectiveActivation = activationDate ?? ADV_ACTIVATION_DATE;
    if (Date.now() < effectiveActivation) {
        return null;
    }
    const loc = {
        popupTitle: "Mở Khóa Tính Năng Phân Tích Pro",
        popupDescription: "Bạn đang sử dụng phiên bản miễn phí VNInvestCharts. Nâng cấp tài khoản để truy cập dữ liệu cá mập và giả lập thị trường không giới hạn.",
        upgradeText: "Tìm hiểu Pro",
        closeText: "Đóng",
        closeCountdownText: (c) => `Đóng (${c}s)`,
        badgePromo: "PROMO",
        badgeAd: "AD",
        imageAlt: "Quảng cáo VNInvest",
        closeAriaLabel: "Đóng",
        ...locale,
    };
    const [tier, setTier] = useState(() => resolvePatTier(pat));
    const [showPopup, setShowPopup] = useState(false);
    const [countdown, setCountdown] = useState(countdownSeconds);
    const [impressionFired, setImpressionFired] = useState(false);
    const [portalRoot, setPortalRoot] = useState(null);
    const cycleTimerRef = useRef(null);
    const countdownTimerRef = useRef(null);
    const popupTimerRef = useRef(null);
    const impressionObserverRef = useRef(null);
    const popupRef = useRef(null);
    const bannerRef = useRef(null);
    // Resolve tier: server validation wins if provided, else client-side parse.
    useEffect(() => {
        let cancelled = false;
        if (validatePat && pat) {
            validatePat(pat)
                .then((serverTier) => {
                if (!cancelled && serverTier)
                    setTier(serverTier);
            })
                .catch(() => {
                if (!cancelled)
                    setTier(resolvePatTier(pat));
            });
        }
        else {
            setTier(resolvePatTier(pat));
        }
        return () => {
            cancelled = true;
        };
    }, [pat, validatePat]);
    // Ensure portal root exists (outside widget DOM).
    useEffect(() => {
        setPortalRoot(getPortalRoot());
    }, []);
    const shouldShowAds = requiresAds(tier);
    const clearAllTimers = useCallback(() => {
        if (cycleTimerRef.current)
            clearTimeout(cycleTimerRef.current);
        if (countdownTimerRef.current)
            clearInterval(countdownTimerRef.current);
        if (popupTimerRef.current)
            clearTimeout(popupTimerRef.current);
        cycleTimerRef.current = null;
        countdownTimerRef.current = null;
        popupTimerRef.current = null;
    }, []);
    const setupImpressionObserver = useCallback((element, trackingUrl) => {
        if (!element || !trackingUrl || impressionFired || typeof IntersectionObserver === "undefined")
            return;
        if (impressionObserverRef.current)
            impressionObserverRef.current.disconnect();
        impressionObserverRef.current = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
                    const startTime = Date.now();
                    const checkVisibility = () => {
                        if (Date.now() - startTime >= 1000) {
                            trackBeacon(trackingUrl);
                            setImpressionFired(true);
                            impressionObserverRef.current?.disconnect();
                        }
                        else if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
                            requestAnimationFrame(checkVisibility);
                        }
                    };
                    requestAnimationFrame(checkVisibility);
                }
            });
        }, { root: null, rootMargin: "0px", threshold: [0.5] });
        impressionObserverRef.current.observe(element);
    }, [impressionFired]);
    const closePopup = useCallback(() => {
        if (countdownTimerRef.current)
            clearInterval(countdownTimerRef.current);
        if (popupTimerRef.current)
            clearTimeout(popupTimerRef.current);
        setShowPopup(false);
        onAdClose?.();
        if (shouldShowAds) {
            cycleTimerRef.current = setTimeout(() => {
                setShowPopup(true);
                setCountdown(countdownSeconds);
                setImpressionFired(false);
                onAdShow?.();
            }, cycleMinutes * 60 * 1000);
        }
    }, [shouldShowAds, cycleMinutes, countdownSeconds, onAdShow, onAdClose]);
    const handleClick = useCallback(() => {
        const destUrl = destinationUrl || campaign?.click_url || "https://vninvest.edusuccess.vn";
        const trackUrl = clickTrackingUrl || campaign?.click_tracking_url;
        if (trackUrl)
            trackBeacon(trackUrl);
        if (typeof window !== "undefined")
            window.open(destUrl, "_blank", "noopener,noreferrer");
        closePopup();
        onAdClick?.();
    }, [campaign, clickTrackingUrl, destinationUrl, closePopup, onAdClick]);
    // Start cycle on mount (or when tier/campaign changes).
    useEffect(() => {
        if (!shouldShowAds)
            return;
        cycleTimerRef.current = setTimeout(() => {
            setShowPopup(true);
            setCountdown(countdownSeconds);
            setImpressionFired(false);
            onAdShow?.();
            countdownTimerRef.current = setInterval(() => {
                setCountdown((prev) => {
                    if (prev <= 1) {
                        if (countdownTimerRef.current)
                            clearInterval(countdownTimerRef.current);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
            popupTimerRef.current = setTimeout(() => {
                closePopup();
            }, POPUP_DISPLAY_DURATION);
        }, 5000);
        return () => {
            clearAllTimers();
            if (impressionObserverRef.current)
                impressionObserverRef.current.disconnect();
        };
    }, [shouldShowAds, campaign, countdownSeconds, onAdShow, closePopup, clearAllTimers]);
    // Impression observers for banner + popup.
    useEffect(() => {
        if (bannerRef.current && campaign?.impression_tracking_url) {
            setupImpressionObserver(bannerRef.current, campaign.impression_tracking_url);
        }
    }, [campaign, setupImpressionObserver]);
    useEffect(() => {
        if (popupRef.current && campaign?.impression_tracking_url) {
            setupImpressionObserver(popupRef.current, campaign.impression_tracking_url);
        }
    }, [campaign, setupImpressionObserver]);
    if (!shouldShowAds || !portalRoot) {
        return null;
    }
    const imageUrl = imageSource?.default || campaign?.image_source?.default;
    return createPortal(_jsxs(_Fragment, { children: [imageUrl ? (_jsx("div", { ref: bannerRef, style: STYLES.bannerSlot, "data-testid": "ads-banner", children: _jsx("a", { href: destinationUrl || campaign?.click_url || "https://vninvest.edusuccess.vn", target: "_blank", rel: "noopener noreferrer", style: STYLES.bannerLink, onClick: (e) => {
                        e.preventDefault();
                        handleClick();
                    }, children: _jsx("img", { src: imageUrl, alt: loc.imageAlt, style: STYLES.bannerImage }) }) })) : null, showPopup && (_jsxs("div", { ref: popupRef, style: STYLES.popup, role: "dialog", "aria-modal": "true", "aria-labelledby": "engine-ads-popup-title", "data-testid": "ads-popup", children: [_jsxs("div", { style: STYLES.popupHeader, children: [_jsxs("div", { style: STYLES.popupBadges, children: [_jsx("span", { style: STYLES.popupBadgePromo, children: loc.badgePromo }), _jsx("span", { style: STYLES.popupBadgeAd, children: loc.badgeAd })] }), _jsx("button", { onClick: closePopup, style: STYLES.popupCloseBtn, "aria-label": loc.closeAriaLabel, children: "\u2715" })] }), campaign?.video_url ? (_jsx("div", { onClick: handleClick, style: STYLES.popupMedia, children: _jsx("video", { src: campaign.video_url, autoPlay: true, muted: true, loop: true, playsInline: true, poster: imageUrl || undefined, style: STYLES.popupMediaImg }) })) : imageUrl ? (_jsx("div", { onClick: handleClick, style: STYLES.popupMedia, children: _jsx("img", { src: imageUrl, alt: loc.imageAlt, style: STYLES.popupMediaImg }) })) : null, _jsxs("div", { style: STYLES.popupInfo, children: [_jsx("h5", { id: "engine-ads-popup-title", style: STYLES.popupTitle, children: loc.popupTitle }), _jsx("p", { style: STYLES.popupDescription, children: loc.popupDescription })] }), _jsxs("div", { style: STYLES.popupActions, children: [_jsx("button", { onClick: handleClick, style: STYLES.popupUpgradeBtn, children: loc.upgradeText }), _jsx("button", { disabled: countdown > 0, onClick: closePopup, style: {
                                    ...STYLES.popupCloseBtnSecondary,
                                    ...(countdown > 0 ? {} : STYLES.popupCloseBtnSecondaryEnabled),
                                }, children: countdown > 0 ? loc.closeCountdownText(countdown) : loc.closeText })] })] }))] }), portalRoot);
}
